import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TeamAvatar } from './pools/poolFx';
import BetLike from './BetLike';
import { CHAT_MAX, demoName, isSocialDemo, parlayLegs, useChat, useLikes } from '../social';
import './chat.css';

const READ_KEY = 'bdb_chat_read';
const readAt = () => { try { return Number(localStorage.getItem(READ_KEY)) || 0; } catch { return 0; } };
const markRead = () => { try { localStorage.setItem(READ_KEY, String(Date.now())); } catch { /* the badge just comes back next visit */ } };

const GLIDE_PULL = 170;          // the list's scroll spring: stronger = quicker
const GLIDE_DAMP = 2 * Math.sqrt(GLIDE_PULL);   // just enough to stop without overshooting
const GROUP_MS = 5 * 60000;      // messages from one person this close together share one avatar and name
const HOLD_MS = 450;             // press this long on a message for its menu
const EASE = 'cubic-bezier(0.37, 0, 0.63, 1)';   // eased between every step of a wobble, so it never jerks
// the message bar's gel-o: the same wobble as a new message, much softer
const BAR_WOBBLE = [
  { transform: 'none', easing: EASE },
  { transform: 'scale(1.025, 0.93)', offset: 0.25, easing: EASE },
  { transform: 'scale(0.99, 1.04)', offset: 0.5, easing: EASE },
  { transform: 'scale(1.006, 0.985)', offset: 0.75, easing: EASE },
  { transform: 'none' },
];
// your bubble landing: the gel-o at the end of its trip from the bar
const FLY_MS = 460;
const LAND_MS = 620;
const LAND_OVERLAP_MS = 60;
const LAND_WOBBLE = [
  { transform: 'none', easing: EASE },
  { transform: 'scale(1.05, 0.95)', offset: 0.25, easing: EASE },
  { transform: 'scale(0.98, 1.025)', offset: 0.55, easing: EASE },
  { transform: 'scale(1.008, 0.993)', offset: 0.8, easing: EASE },
  { transform: 'none' },
];
const EMOJIS = ['🔥', '😂', '💀', '🐐', '🎯', '💰', '👀', '🥶'];

const dayOf = (at) => new Date(at).toDateString();
function dayLabel(at, fr) {
  const d = new Date(at);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (d.toDateString() === today.toDateString()) return fr ? 'Aujourd’hui' : 'Today';
  if (d.toDateString() === yesterday.toDateString()) return fr ? 'Hier' : 'Yesterday';
  return d.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'long', day: 'numeric' });
}
const clock = (at, fr) => new Date(at).toLocaleTimeString(fr ? 'fr-CA' : 'en-CA', { hour: 'numeric', minute: '2-digit' });
const likeKeyOf = (m) => (m.bet ? `bet:${m.bet.id}` : `chat:${m.id}`);

// a bet shared in the chat. Its flame is the bet's own: the same likes as the Feed and the Leaderboard
function BetCard({ bet, gameInfo, betText, money, fr, like }) {
  const parlay = bet.bet_type === 'parlay';
  const game = parlay ? null : gameInfo(bet);
  const pick = parlay ? (fr ? `Combiné de ${parlayLegs(bet)} sélections` : `${parlayLegs(bet)}-leg parlay`) : betText(bet);
  const team = game && pick ? [game.home, game.away].find((t) => t?.name && pick.includes(t.name)) : null;
  const won = bet.status === 'settled' && bet.result === 'won';
  const lost = bet.status === 'settled' && bet.result === 'lost';
  const body = (
    <>
      <div className="chat-betcard-top">
        {team?.logo ? <img src={team.logo} alt="" /> : <span className="chat-betcard-mono">{parlay ? `${parlayLegs(bet)}×` : 'BDB'}</span>}
        <div>
          <b>{pick}</b>
          {!parlay && bet.game_description && <small>{bet.game_description}</small>}
        </div>
      </div>
      <div className="chat-betcard-money">
        <span>{money(+bet.stake)} @ {(+bet.odds).toFixed(2)}</span>
        <b className={won ? 'won' : lost ? 'lost' : ''}>
          {won ? `${fr ? 'Gagné' : 'Won'} ${money(+bet.payout)}` : lost ? (fr ? 'Perdu' : 'Lost') : `${fr ? 'Pour gagner' : 'To win'} ${money(+bet.potential_win || +bet.stake * +bet.odds)}`}
        </b>
      </div>
    </>
  );
  return like ? <BetLike className={`chat-betcard${like.count ? ' has-likes' : ''}`} {...like}>{body}</BetLike> : <div className="chat-betcard">{body}</div>;
}

// the chat room: a panel on the right on a computer, the whole screen on a phone
export default function Chat({ open, onClose, onUnread, profile, players, avatarOf, lang, myBets = [], gameInfo, betText, money }) {
  const fr = lang === 'fr';
  const { messages, send, remove, failed, online, typing, notifyTyping } = useChat(profile?.id);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(false);
  const [lastRead, setLastRead] = useState(readAt);
  const [replyTo, setReplyTo] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [tray, setTray] = useState(null);       // 'bets' | 'emoji' above the message box
  const [trayClosing, setTrayClosing] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const barRef = useRef(null);
  const nearBottom = useRef(true);
  const justOpened = useRef(false);
  const glide = useRef(null);
  const hold = useRef(null);
  const seen = useRef(new Set());
  const fresh = useRef(new Set());
  const flown = useRef(new Set());
  const isAdmin = profile?.username === 'CL34RY';

  const peopleById = useMemo(() => Object.fromEntries((players || []).map((p) => [p.id, p])), [players]);
  const nameOf = (id) => peopleById[id]?.username || demoName(id) || (fr ? 'Joueur' : 'Player');
  const messageById = useMemo(() => Object.fromEntries(messages.map((m) => [m.id, m])), [messages]);

  const likeKeys = useMemo(() => messages.map(likeKeyOf), [messages]);
  const { byKey: likes, toggle } = useLikes(likeKeys, profile?.id, open, 'chat');
  const likeProps = (key) => {
    const list = likes[key] || [];
    return { liked: list.some((l) => l.user_id === profile?.id), count: list.length, onToggle: () => toggle(key) };
  };

  // the badge on the Chat button: messages from the others since you last had the chat open
  useEffect(() => {
    if (open) { markRead(); setLastRead(Date.now()); onUnread(0); return; }
    onUnread(messages.filter((m) => m.user_id !== profile?.id && new Date(m.created_at).getTime() > lastRead).length);
  }, [open, messages, lastRead, profile?.id, onUnread]);

  // opening the chat lands on the newest message; after that, stay put if you scrolled up to read
  useEffect(() => {
    if (open) { nearBottom.current = true; justOpened.current = true; }
    return () => { cancelAnimationFrame(glide.current); glide.current = null; };
  }, [open]);

  // new messages: the list glides up to the bottom instead of jumping there. Scrolling by hand stops it
  const stopGlide = () => { cancelAnimationFrame(glide.current); glide.current = null; };
  const glideDown = useRef(null);
  glideDown.current = () => {
    const list = listRef.current;
    if (!list || glide.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { list.scrollTop = list.scrollHeight; return; }
    // a spring: it starts from a standstill, speeds up, then eases to a stop, and a new message just bends its path
    let top = list.scrollTop;
    let speed = 0;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min((now - last) / 1000, 0.032);
      last = now;
      const target = list.scrollHeight - list.clientHeight;
      speed += ((target - top) * GLIDE_PULL - speed * GLIDE_DAMP) * dt;
      top += speed * dt;
      if (Math.abs(target - top) < 0.5 && Math.abs(speed) < 8) { list.scrollTop = target; glide.current = null; return; }
      list.scrollTop = top;
      glide.current = requestAnimationFrame(step);
    };
    glide.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    if (!open || !nearBottom.current || !listRef.current) return;
    if (justOpened.current) { justOpened.current = false; listRef.current.scrollTop = listRef.current.scrollHeight; return; }
    glideDown.current();
  }, [open, messages]);
  // flames show up just after their message and make it taller: keep following the bottom when the list grows
  useEffect(() => {
    const list = listRef.current;
    if (!open || !list?.firstElementChild) return undefined;
    const watcher = new ResizeObserver(() => { if (nearBottom.current) glideDown.current(); });
    watcher.observe(list.firstElementChild);
    return () => watcher.disconnect();
  }, [open]);

  // only messages that arrive while the chat is open bounce in, not the ones already there when it opens
  messages.forEach((m) => {
    if (open && !seen.current.has(m.id)) fresh.current.add(m.id);
    seen.current.add(m.id);
  });
  if (!open) { fresh.current.clear(); }

  // your message leaves the message bar: a copy of the bubble flies from the bar to where the real one sits.
  // A copy, because the list cuts off anything below it
  useLayoutEffect(() => {
    const m = messages[messages.length - 1];
    const list = listRef.current;
    if (!open || !m || !list || !barRef.current || m.user_id !== profile?.id || m.bet || !fresh.current.has(m.id) || flown.current.has(m.id)) return;
    flown.current.add(m.id);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const bubble = list.querySelector(`[data-msg="${m.id}"] .chat-bubble`);
    if (!bubble) return;
    const to = bubble.getBoundingClientRect();
    const bar = barRef.current.getBoundingClientRect();
    const look = getComputedStyle(bubble);
    const barLook = getComputedStyle(barRef.current);
    const endBottom = to.bottom;

    // three layers, so the browser only moves pictures and never recalculates the page:
    // the outer one travels, the shape is cut from the bar's size down to the bubble's, the words slide on top
    const w = Math.max(bar.width, to.width);
    const h = Math.max(bar.height, to.height);
    const copy = bubble.cloneNode(true);
    copy.querySelectorAll('.bet-like, .bet-like-big').forEach((x) => x.remove());
    const words = document.createElement('div');
    words.className = 'chat-ghost-words';
    words.append(...copy.children);
    if (to.height < 45) words.style.whiteSpace = 'pre';
    Object.assign(words.style, { left: `${w - to.width + 11}px`, width: `${to.width - 22}px` });
    const shape = document.createElement('div');
    shape.className = 'chat-ghost-shape';
    shape.append(words);
    const ghost = document.createElement('div');
    ghost.className = 'chat-ghost';
    Object.assign(ghost.style, { left: `${to.right - w}px`, top: `${endBottom - h}px`, width: `${w}px`, height: `${h}px` });
    ghost.append(shape);
    bubble.style.visibility = 'hidden';
    // the bubble's real spot moves with the chat's scroll: the copy rides along, so they move as one
    const anchor = document.createElement('div');
    anchor.className = 'chat-ghost-anchor';
    anchor.append(ghost);
    list.parentElement.appendChild(anchor);
    let following;
    const follow = () => {
      anchor.style.transform = `translateY(${bubble.getBoundingClientRect().bottom - endBottom}px)`;
      following = requestAnimationFrame(follow);
    };
    follow();

    // it's at full speed halfway up, and that's where it has finished shrinking to the message's size
    const travel = { duration: FLY_MS, easing: 'cubic-bezier(0.4, 0, 0.6, 1)', fill: 'both' };
    const shrink = { duration: FLY_MS / 2, easing: 'ease-out', fill: 'both' };
    ghost.animate([{ transform: `translate(${bar.right - to.right}px, ${bar.bottom - endBottom}px)` }, { transform: 'none' }], travel);
    shape.animate([
      { clipPath: `inset(${h - bar.height}px 0 0 ${w - bar.width}px round 20px)`, backgroundColor: barLook.backgroundColor },
      { clipPath: `inset(${h - to.height}px 0 0 ${w - to.width}px round ${look.borderTopLeftRadius})`, backgroundColor: look.backgroundColor },
    ], shrink);
    words.animate([
      { transform: `translate(${to.width - bar.width + 3}px, ${7 - (bar.height - 19) / 2}px) scale(${16 / 14})`, color: barLook.color },
      { transform: 'none', color: look.color },
    ], shrink);
    // the bounce starts as it lands, so the two run together with no stop in between
    shape.animate(LAND_WOBBLE, { duration: LAND_MS, delay: FLY_MS - LAND_OVERLAP_MS }).onfinish = () => {
      cancelAnimationFrame(following);
      bubble.style.visibility = '';
      anchor.remove();
    };
  }, [open, messages, profile?.id]);

  if (!open) return null;

  const post = async (text, extra) => {
    setError(false);
    nearBottom.current = true;
    const ok = await send(text, { replyTo: replyTo?.id ?? null, ...extra });
    if (ok) setReplyTo(null);
    else setError(true);
    return ok;
  };

  const wobbleBar = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    barRef.current?.animate(BAR_WOBBLE, { duration: 500 });
  };

  const submit = async (e) => {
    e.preventDefault();
    const text = draft;
    if (!text.trim()) return;
    setDraft('');
    setTray(null);
    if (!(await post(text))) setDraft(text);
  };

  const shareBet = async (bet) => {
    setTray(null);
    await post('', { bet });
  };

  const startHold = (id) => {
    clearTimeout(hold.current);
    hold.current = setTimeout(() => setMenuId(id), HOLD_MS);
  };
  const endHold = () => clearTimeout(hold.current);

  const reply = (m) => { setReplyTo(m); setMenuId(null); inputRef.current?.focus(); };
  const copy = (m) => { navigator.clipboard?.writeText(m.body).catch(() => {}); setMenuId(null); };

  const onlinePeople = online.slice(0, 4);

  // closing plays its animation first; the tray goes away when that ends
  const toggleTray = (name) => {
    if (tray === name && !trayClosing) { setTrayClosing(true); return; }
    setTrayClosing(false);
    setTray(name);
  };
  const trayEnded = (e) => {
    if (e.target === e.currentTarget && trayClosing) { setTray(null); setTrayClosing(false); }
  };

  return (
    <aside className="chat-panel" aria-label="Chat">
      <div className="chat-head">
        <div className="chat-head-text">
          <h3>Chat</h3>
          {online.length > 0 && <span className="chat-online"><i />{online.length} {fr ? 'en ligne' : 'online'}</span>}
        </div>
        <div className="chat-faces" aria-hidden="true">
          {onlinePeople.map((id) => (
            <TeamAvatar key={id} team={peopleById[id] ? avatarOf(peopleById[id]) : null} name={nameOf(id)} size={28} />
          ))}
        </div>
        <button type="button" className="chat-close" onClick={onClose} aria-label={fr ? 'Fermer' : 'Close'}>×</button>
      </div>
      {isSocialDemo() && <p className="chat-demo">{fr ? 'DÉMO · faux messages, rien n’est sauvegardé' : 'DEMO · fake messages, nothing saved'}</p>}

      <div
        className="chat-list"
        ref={listRef}
        onClick={() => { if (menuId !== null) setMenuId(null); }}
        onWheel={stopGlide}
        onTouchStart={stopGlide}
        onScroll={(e) => {
          if (glide.current) return;   // our own glide, not the reader moving
          const el = e.currentTarget;
          nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
          endHold();
        }}
      >
        <div className="chat-scroll-body">
        {failed && <p className="chat-empty">{fr ? 'Le chat ne peut pas se charger.' : 'The chat could not load.'}</p>}
        {!failed && messages.length === 0 && <p className="chat-empty">{fr ? 'Sois le premier à écrire!' : 'Be the first to say something!'}</p>}
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const next = messages[i + 1];
          const newDay = !prev || dayOf(prev.created_at) !== dayOf(m.created_at);
          const day = newDay && <div className="chat-day"><span>{dayLabel(m.created_at, fr)}</span></div>;

          const mine = m.user_id === profile?.id;
          const joins = (a, b) => a && b && a.user_id === b.user_id
            && new Date(b.created_at) - new Date(a.created_at) < GROUP_MS && dayOf(a.created_at) === dayOf(b.created_at);
          const first = !joins(prev, m) || !!m.reply_to;
          const last = !joins(m, next) || !!next?.reply_to;
          const name = nameOf(m.user_id);
          const person = peopleById[m.user_id];
          const quoted = m.reply_to ? messageById[m.reply_to] : null;
          const key = likeKeyOf(m);
          const like = likeProps(key);

          return (
            <React.Fragment key={m.id}>
              {day}
              <div className={`chat-msg${mine ? ' mine' : ''}${first ? ' first' : ''}${last ? ' last' : ''}${fresh.current.has(m.id) && (!mine || m.bet) ? ' is-new' : ''}`} data-msg={m.id}>
                {!mine && (last
                  ? <TeamAvatar team={person ? avatarOf(person) : null} name={name} size={28} />
                  : <span className="chat-avatar-gap" />)}
                <div
                  className="chat-col"
                  onPointerDown={() => startHold(m.id)}
                  onPointerUp={endHold}
                  onPointerLeave={endHold}
                  onPointerMove={(e) => { if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) endHold(); }}
                  onContextMenu={(e) => { e.preventDefault(); setMenuId(m.id); }}
                >
                  {first && !mine && <b className="chat-name">{name}</b>}
                  {m.bet ? (
                    <BetCard bet={m.bet} gameInfo={gameInfo} betText={betText} money={money} fr={fr} like={like} />
                  ) : (
                    <BetLike className={`chat-bubble${like.count ? ' has-likes' : ''}`} {...like}>
                      {m.reply_to && (
                        <div className="chat-quote">
                          <b>{quoted ? nameOf(quoted.user_id) : ''}</b>
                          <span>{quoted ? (quoted.bet ? (fr ? 'Pari partagé' : 'Shared bet') : quoted.body) : (fr ? 'Message supprimé' : 'Message deleted')}</span>
                        </div>
                      )}
                      <p>{m.body}</p>
                    </BetLike>
                  )}
                  {last && <time>{clock(m.created_at, fr)}</time>}
                  <button type="button" className="chat-more" aria-label={fr ? 'Options' : 'Options'} onClick={(e) => { e.stopPropagation(); setMenuId(menuId === m.id ? null : m.id); }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                  </button>
                  {menuId === m.id && (
                    <div className={`chat-menu${i >= messages.length - 2 ? ' up' : ''}`} role="menu" onClick={(e) => e.stopPropagation()}>
                      <button type="button" role="menuitem" onClick={() => { like.onToggle(); setMenuId(null); }}>{like.liked ? (fr ? 'Retirer la flamme' : 'Remove flame') : (fr ? 'Flamme' : 'Flame')}</button>
                      <button type="button" role="menuitem" onClick={() => reply(m)}>{fr ? 'Répondre' : 'Reply'}</button>
                      {!m.bet && <button type="button" role="menuitem" onClick={() => copy(m)}>{fr ? 'Copier' : 'Copy'}</button>}
                      {isAdmin && <button type="button" role="menuitem" className="danger" onClick={() => { remove(m.id); setMenuId(null); }}>{fr ? 'Supprimer' : 'Delete'}</button>}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {typing.length > 0 && (
          <div className="chat-msg first last chat-typing">
            <TeamAvatar team={peopleById[typing[0]] ? avatarOf(peopleById[typing[0]]) : null} name={nameOf(typing[0])} size={28} />
            <div className="chat-col">
              <b className="chat-name">{typing.map(nameOf).join(', ')}</b>
              <div className="chat-dots" aria-label={fr ? 'En train d’écrire' : 'Writing'}><i /><i /><i /></div>
            </div>
          </div>
        )}
        </div>
      </div>

      {error && <p className="chat-error">{fr ? 'Message non envoyé. Réessaie.' : 'Message not sent. Try again.'}</p>}

      <div className="chat-bottom">
      {tray === 'bets' && (
        <div className={`chat-tray chat-tray-bets${trayClosing ? ' closing' : ''}`} onAnimationEnd={trayEnded}>
          <h4>{fr ? 'Partager un pari' : 'Share a bet'}</h4>
          {myBets.length === 0 && <p className="chat-empty">{fr ? 'Aucun pari en cours.' : 'No open bets.'}</p>}
          {myBets.map((b, i) => (
            <button type="button" key={b.id} style={{ '--i': Math.min(i, 6) }} onClick={() => shareBet(b)}>
              <BetCard bet={b} gameInfo={gameInfo} betText={betText} money={money} fr={fr} />
            </button>
          ))}
        </div>
      )}
      {tray === 'emoji' && (
        <div className={`chat-tray chat-tray-emoji${trayClosing ? ' closing' : ''}`} onAnimationEnd={trayEnded}>
          {EMOJIS.map((e, i) => <button type="button" key={e} style={{ '--i': i }} onClick={() => { setDraft((d) => (d + e).slice(0, CHAT_MAX)); inputRef.current?.focus(); }}>{e}</button>)}
        </div>
      )}
      {replyTo && (
        <div className="chat-replying">
          <div>
            <b>{fr ? 'Réponse à' : 'Replying to'} {nameOf(replyTo.user_id)}</b>
            <span>{replyTo.bet ? (fr ? 'Pari partagé' : 'Shared bet') : replyTo.body}</span>
          </div>
          <button type="button" onClick={() => setReplyTo(null)} aria-label={fr ? 'Annuler' : 'Cancel'}>×</button>
        </div>
      )}
      <form className="chat-form" onSubmit={submit}>
        <button type="button" className={`chat-tool${tray === 'bets' && !trayClosing ? ' on' : ''}`} onClick={() => toggleTray('bets')} aria-label={fr ? 'Partager un pari' : 'Share a bet'}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <div className="chat-input" ref={barRef}>
          <input ref={inputRef} onFocus={wobbleBar} value={draft} onChange={(e) => { setDraft(e.target.value); if (e.target.value.trim()) notifyTyping(); }} maxLength={CHAT_MAX} placeholder={fr ? 'Écris un message…' : 'Write a message…'} aria-label="Message" />
          <button type="button" className="chat-emoji" onClick={() => toggleTray('emoji')} aria-label="Emoji">🔥</button>
        </div>
        <button type="submit" className="chat-send" disabled={!draft.trim()} aria-label={fr ? 'Envoyer' : 'Send'}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" /></svg>
        </button>
      </form>
      </div>
    </aside>
  );
}
